import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Hook that sets up realtime subscriptions for dashboard-wide updates.
 * Listens to changes in feedback_pages, profiles, and notification_preferences
 * and invalidates relevant React Query caches to trigger live updates.
 */
export function useRealtimeDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('dashboard-realtime')
      // Listen for feedback_pages changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feedback_pages',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) {
            console.log('Feedback pages changed:', payload.eventType);
          }
          // Invalidate pages-related queries
          queryClient.invalidateQueries({ queryKey: ['feedback-pages'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
          queryClient.invalidateQueries({ queryKey: ['pages-needing-attention'] });
        }
      )
      // Listen for profile changes
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) {
            console.log('Profile changed:', payload.eventType);
          }
          // Invalidate profile-related queries
          queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
          queryClient.invalidateQueries({ queryKey: ['settings-data'] });
        }
      )
      // Listen for notification preferences changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_preferences',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) {
            console.log('Notification preferences changed:', payload.eventType);
          }
          // Invalidate notification-related queries
          queryClient.invalidateQueries({ queryKey: ['notification-preferences', user.id] });
          queryClient.invalidateQueries({ queryKey: ['settings-data'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);
}
